import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useLocation } from "react-router-dom";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isCompactNav, isPortrait, isPhoneLandscape } = useResponsive();
  // Hamburger: any compact viewport (<1100px), portrait orientation, or short landscape phones.
  const showHamburger = isCompactNav || isPortrait || isPhoneLandscape;

  // Public/auth pages where menu items should be hidden
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";

  // Only show app menu items if authenticated AND not on auth pages
  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    setIsOpen(false);
    clearAuthState();
    setIsAuthenticated(false);
    window.location.replace("/login");
  };

  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  // Authenticated app menu — X20 Founder subject: lines of revenue.
  // All currently route to "/" (the shell Dashboard); add routes in App.js as
  // pages are built out.
  const menuLinks = [
    { name: "Revenue Lines", path: "/" },
    { name: "Products", path: "/" },
    { name: "Forecasts", path: "/" },
    { name: "Job Hunt", path: "/jobs" },
    { name: "Prospects", path: "/prospects" },
    { name: "x20 Family", path: "/" },
    { name: "About", path: "/" },
    { name: "Help", path: "/" },
  ];

  // Unauthenticated visitor menu — mirrors menuLinks.
  const publicMenuLinks = [
    { name: "Revenue Lines", path: "/login" },
    { name: "Products", path: "/login" },
    { name: "x20 Family", path: "/login" },
    { name: "About", path: "/login" },
  ];

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return isActive
                ? `font-bold ${sizeCls} hover:opacity-90 focus-visible:opacity-90`
                : `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: theme.accentColor || "#FFA72A" }}
          >
            X20 Founder
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none p-2 rounded-md"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#FFF",
            }}
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Desktop Menu */}
        <nav
          className={showHamburger ? "hidden" : "flex space-x-4 items-center"}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link, idx) => (
                <NavLink
                  key={`${link.name}-${idx}`}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{ color: theme.accentColor || "#FFA72A" }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className="text-[120%] hover:opacity-90 focus:opacity-90"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link, idx) => (
                <NavLink
                  key={`${link.name}-${idx}`}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{ color: theme.accentColor || "#FFA72A" }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Login
              </NavLink>
              <NavLink
                to="/register"
                style={{
                  backgroundColor: theme.accentColor || "#FFA72A",
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "6px 20px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav
          id="mobile-nav"
          className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}
          style={{ borderTop: `1px solid ${theme.accentColor || "#FFA72A"}22` }}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link, idx) => (
                <NavLink
                  key={`${link.name}-${idx}`}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className="block py-3 px-4 text-[120%] border-b"
                  style={{
                    color: theme.accentColor || "#FFA72A",
                    borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className="block py-3 px-4 text-[120%]"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link, idx) => (
                <NavLink
                  key={`${link.name}-${idx}`}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className="block py-3 px-4 text-[120%] border-b"
                  style={{
                    color: theme.accentColor || "#FFA72A",
                    borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                onClick={handleMenuItemClick}
                className="block py-3 px-4 text-[120%] border-b"
                style={{
                  color: theme.accentColor || "#FFA72A",
                  borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                }}
              >
                Login
              </NavLink>
              <NavLink
                to="/register"
                onClick={handleMenuItemClick}
                className="block py-3 px-4 text-[120%] font-semibold"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
